/*======================================================================================================================
  * Bluebell: src/app/state/tasks/tasks.effects.ts
  * Last Edited: 18/06/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import { ProjectListData } from '@state/projects/project.interfaces';
import { AccountTasks, Task } from '@state/tasks/tasks.interfaces';
import { mergeMap, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  loadAccountTasks,
  loadAccountTasksSuccess,
  loadAccountTasksFailure,
  saveTask,
  saveTaskSuccess,
  saveTaskFailure,
  deleteTask,
  deleteTaskSuccess,
  deleteTaskFailure,
  loadAllProjects,
  loadAllProjectsSuccess,
  loadAllProjectsFailure,
} from './tasks.actions';

@Injectable()
export class TasksEffects {
  private actions$ = inject(Actions);
  private api = inject(ApiService);
  private handler = inject(EffectHandlerService);


  getTasks$ = createEffect(() => this.actions$.pipe(
    ofType(loadAccountTasks),
    switchMap(() => {
      return this.api.listTasks().pipe(
        this.handler.effectResponse({
          retry: loadAccountTasks(),
          success: (accountTasks: AccountTasks[]) => loadAccountTasksSuccess({accountTasks: accountTasks}),
          failure: loadAccountTasksFailure(),
          failureMessage: 'Could not load account tasks'
        })
      );
    })
  ));

  saveTask$ = createEffect(() => this.actions$.pipe(
    ofType(saveTask),
    map((action: {task: Task}) => action.task),
    switchMap((task: Task) => (
      task.uuid
        ? this.api.updateTask(task)
        : this.api.createTask(task)
      ).pipe(
      this.handler.effectResponse({
        retry: saveTask({task: task}),
        success: (taskResponse: Task) => saveTaskSuccess({task: taskResponse}),
        failure: saveTaskFailure(),
        failureMessage: 'Could not save task'
      })
      )
    )
  ));

  deleteTask$ = createEffect(() => this.actions$.pipe(
    ofType(deleteTask),
    map((action: {task: Task}) => action.task),
    mergeMap((task: Task) => this.api.deleteTask(task.uuid).pipe(
      this.handler.effectResponse({
        retry: deleteTask({ task: task }),
        success: () => deleteTaskSuccess({task: task}),
        failure: deleteTaskFailure(),
        failureMessage: 'Could not delete task'
      })
    ))
  ));

  loadAllProjects$ = createEffect(() => this.actions$.pipe(
    ofType(loadAllProjects),
    switchMap(() => this.api.listProjects({ project_list: true }, true).pipe(
      this.handler.effectResponse({
        retry: loadAllProjects(),
        success: (projectsList: ProjectListData[]) => loadAllProjectsSuccess({projectsList: projectsList}),
        failure: ({ error }: HttpErrorResponse) => loadAllProjectsFailure({error}),
        failureMessage: 'Could not load projects'
      })
    ))
  ));
}
